import fixtureJson from "./daily-briefing.fixture.json";
import type {ConfidenceDimension, DailyBriefingFixture, Opportunity, OpportunityFact} from "./briefingTypes";

/**
 * The fixture is the page's only data source. If a value is not in the fixture,
 * it does not render.
 */
export const BRIEFING: DailyBriefingFixture = fixtureJson as unknown as DailyBriefingFixture;

/**
 * The four confidence dimensions in the order fixed by the fixture's confidence
 * model: scope → commercial basis → current activity → future conditions.
 */
export const CONFIDENCE_DIMENSIONS: ConfidenceDimension[] = BRIEFING.confidence_model.dimensions
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(d => d.id);

/** Human label of a confidence dimension, straight from the fixture's model. */
export function dimensionLabel(dimension: ConfidenceDimension): string {
    return BRIEFING.confidence_model.dimensions.find(d => d.id === dimension)?.label ?? dimension;
}

export function dimensionQuestion(dimension: ConfidenceDimension): string | undefined {
    return BRIEFING.confidence_model.dimensions.find(d => d.id === dimension)?.question;
}

/**
 * Confidence is the MIN of the four dimension scores — weakest link, never the
 * average, so three strong dimensions can't hide one fatal weakness.
 */
export function opportunityConfidence(opportunity: Opportunity): number {
    return Math.min(...CONFIDENCE_DIMENSIONS.map(d => opportunity.confidence[d].score));
}

/** The readiness gate: material impact, sufficient confidence, strategy alignment. */
export function isReadyToAct(opportunity: Opportunity): boolean {
    return opportunity.state === 'ready_to_act'
        && opportunityConfidence(opportunity) >= BRIEFING.ready_to_act_gate.confidence_threshold;
}

/** Ordering layer — cost of waiting ranks (euros per week, descending). */
export function orderedOpportunities(): Opportunity[] {
    return BRIEFING.opportunities
        .filter(isReadyToAct)
        .slice()
        .sort((a, b) => b.cost_of_waiting.amount_per_week - a.cost_of_waiting.amount_per_week);
}

/** The two inline label/value pairs of the facts row (impact + timing), in slot order. */
export function factsRow(opportunity: Opportunity): OpportunityFact[] {
    return opportunity.facts
        .filter(f => f.render_hint === 'facts_row')
        .slice()
        .sort((a, b) => a.slot - b.slot);
}

/** Confidence is a qualifier, not a fact — it renders as the monochrome badge. */
export function confidenceBadge(opportunity: Opportunity): OpportunityFact | undefined {
    return opportunity.facts.find(f => f.render_hint === 'badge' && f.type === 'confidence');
}

/**
 * Age in days of a confidence dimension: the OLDEST fact freshness in it,
 * measured against the fixture's session date (never the wall clock).
 */
export function dimensionFreshnessAgeDays(opportunity: Opportunity, dimension: ConfidenceDimension): number {
    const today = Date.parse(BRIEFING.session.today);
    const oldest = Math.min(...opportunity.confidence[dimension].facts.map(f => Date.parse(f.freshness)));
    return Math.max(0, Math.round((today - oldest) / (24 * 60 * 60 * 1000)));
}
